import {
  TbApps,
  TbArrowUp,
  TbBook,
  TbCalendar,
  TbChevronLeft,
  TbChevronRight,
  TbCircleArrowDown,
  TbClock,
  TbDeviceDesktop,
  TbFolderFilled,
  TbHome,
  TbMail,
  TbMap,
  TbMinus,
  TbMusic,
  TbNotes,
  TbPhoto,
  TbPlus,
  TbPointer,
  TbSearch,
  TbSquare,
  TbX,
} from "solid-icons/tb";
import { type Component, For, type JSX, type ParentComponent, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { ScaledFrame } from "~/components/marketing/mockups";
import { useScene } from "~/hooks/useScene";
import "./installSteps.css";

const CHIEF_ICON = "/images/chief-app-icon.png";
const CHIEF_CROWN = "/images/chief-logo-gold-crown-only.svg";

type Glyph = typeof TbApps;

const Scene: ParentComponent<{ label: string; delay: number }> = (props) => {
  const scene = useScene();
  return (
    <div
      ref={scene.ref}
      role="img"
      aria-label={props.label}
      class="text-start select-none"
      classList={{ "mk-armed": scene.armed(), "in-view": scene.inView() }}
      style={{ "--dl-delay": `${props.delay}s` }}
    >
      <ScaledFrame chrome="app" width={256} height={176}>
        <div class="mk-app dl-stage absolute inset-0 text-primary-text">{props.children}</div>
      </ScaledFrame>
    </div>
  );
};

const Pointer: Component<{ class: string }> = (props) => (
  <span class={`pointer-events-none absolute z-10 origin-top-left ${props.class}`}>
    <TbPointer class="size-4 fill-primary-text text-tile drop-shadow" aria-hidden="true" />
  </span>
);

const TrafficLights = () => (
  <span class="flex gap-1">
    <span class="size-1.75 rounded-full bg-mock-close" />
    <span class="size-1.75 rounded-full bg-mock-minimize" />
    <span class="size-1.75 rounded-full bg-mock-zoom" />
  </span>
);

const FINDER_PLACES: { label: string; icon: Glyph }[] = [
  { label: "Applications", icon: TbApps },
  { label: "Desktop", icon: TbDeviceDesktop },
  { label: "Downloads", icon: TbCircleArrowDown },
];

const Finder: ParentComponent<{ place: "Applications" | "Downloads" }> = (props) => (
  <div class="dl-window absolute inset-2.5 flex overflow-hidden rounded-[9px] bg-tile">
    <div class="dl-sidebar flex w-20 shrink-0 flex-col gap-px border-e border-primary-border px-1.5 pt-2">
      <span class="ps-1">
        <TrafficLights />
      </span>
      <span class="mt-3 mb-0.5 ps-1 text-[6.5px] font-semibold text-muted-text">Favorites</span>
      <For each={FINDER_PLACES}>
        {(place) => (
          <span
            class="flex h-3.5 items-center gap-1 rounded-sm px-1 text-[7.5px]"
            classList={{ "bg-primary-text/10": place.label === props.place }}
          >
            <Dynamic component={place.icon} class="size-2.5 shrink-0 text-brand dark:text-brand-border" />
            {place.label}
          </span>
        )}
      </For>
    </div>
    <div class="flex min-w-0 flex-1 flex-col">
      <div class="flex h-5.5 shrink-0 items-center gap-0.5 px-2 text-muted-text">
        <TbChevronLeft class="size-2.5" aria-hidden="true" />
        <TbChevronRight class="size-2.5 opacity-40" aria-hidden="true" />
        <span class="ms-1.5 text-[8.5px] font-semibold text-primary-text">{props.place}</span>
      </div>
      <div class="relative min-h-0 flex-1 px-2 pt-1.5">{props.children}</div>
    </div>
  </div>
);

/** A Finder icon-view item; `picked` is the one the cursor double-clicks. */
const FinderItem: ParentComponent<{ label: string; picked?: boolean }> = (props) => (
  <span class="relative flex flex-col items-center gap-0.5">
    <span class="relative grid size-8 place-items-center">
      <Show when={props.picked}>
        <span class="dl-select dl-anim absolute inset-0 rounded-[5px] bg-primary-text/10" />
      </Show>
      <span class="relative" classList={{ "dl-bounce dl-anim": props.picked }}>
        {props.children}
      </span>
      <Show when={props.picked}>
        <Pointer class="dl-cursor dl-anim top-1/2 left-1/2" />
      </Show>
    </span>
    <span class="relative px-1 text-[7px] leading-2.5 whitespace-nowrap">
      {props.label}
      <Show when={props.picked}>
        <span class="dl-select dl-anim absolute inset-0 rounded-[3px] bg-brand px-1 text-on-brand">{props.label}</span>
      </Show>
    </span>
  </span>
);

const AppTile: Component<{ icon: Glyph; from: string; to: string }> = (props) => (
  <span
    class="grid size-6 place-items-center rounded-md text-white shadow-sm"
    style={{ background: `linear-gradient(${props.from}, ${props.to})` }}
  >
    <Dynamic component={props.icon} class="size-3.5" stroke-width={1.75} aria-hidden="true" />
  </span>
);

const ChiefTile: Component<{ class: string }> = (props) => (
  <img src={CHIEF_ICON} alt="" class={`shadow-sm ${props.class}`} draggable={false} />
);

const DiskImage = () => (
  <span class="relative block h-6 w-7.5 rounded-sm border border-black/15 bg-linear-to-b from-white to-mock-disk shadow-sm">
    <img src={CHIEF_CROWN} alt="" class="absolute top-1 left-1/2 h-2.5 -translate-x-1/2" />
    <span class="absolute inset-x-0 bottom-0 h-1.5 rounded-b-[3px] border-t border-black/10 bg-black/5" />
    <span class="absolute right-1 bottom-0.5 size-0.5 rounded-full bg-mock-online" />
  </span>
);

const DocFile: Component<{ kind: string; color: string }> = (props) => (
  <span class="relative flex h-7 w-5.5 flex-col gap-0.5 rounded-xs border border-black/10 bg-white px-1 pt-1.5 shadow-sm">
    <span class="h-px bg-black/15" />
    <span class="h-px bg-black/15" />
    <span class="h-px w-2/3 bg-black/15" />
    <span
      class="absolute bottom-1 left-1/2 -translate-x-1/2 rounded-[1px] px-0.5 text-[4px] leading-1.5 font-bold text-white"
      style={{ "background-color": props.color }}
    >
      {props.kind}
    </span>
  </span>
);

const PhotoFile = () => (
  <span class="relative block h-5.5 w-7 overflow-hidden rounded-xs border-2 border-white bg-linear-to-b from-mock-sky to-mock-sky-deep shadow-sm">
    <span
      class="absolute inset-x-0 bottom-0 h-3 bg-mock-hill"
      style={{ "clip-path": "polygon(0 100%, 0 55%, 30% 10%, 55% 60%, 75% 30%, 100% 70%, 100% 100%)" }}
    />
  </span>
);

const FolderGlyph = () => (
  <svg viewBox="0 0 48 40" class="h-auto w-11" aria-hidden="true">
    <path d="M4 7a3 3 0 0 1 3-3h11l4 4h19a3 3 0 0 1 3 3v22a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3z" fill="#4aa6ee" />
    <rect x="3" y="11" width="42" height="26" rx="3" fill="#7cc5fb" />
    <rect
      x="3"
      y="11"
      width="42"
      height="26"
      rx="3"
      fill="#2f8fe0"
      opacity="0.35"
      style={{ "mix-blend-mode": "multiply" }}
    />
    <path
      d="M18 31l6-14l6 14m-9.5-5h7"
      fill="none"
      stroke="#1d6fc0"
      stroke-opacity="0.55"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);

const WinWindow: ParentComponent<{ title: string; icon: JSX.Element }> = (props) => (
  <div class="dl-window dl-win absolute inset-2.5 flex flex-col overflow-hidden rounded-[7px] bg-tile">
    <div class="flex h-5.5 shrink-0 items-center gap-1.5 ps-2 text-[7.5px]">
      {props.icon}
      {props.title}
      <span class="ms-auto flex h-full text-secondary-text">
        <span class="grid w-6 place-items-center">
          <TbMinus class="size-2.5" stroke-width={1.25} aria-hidden="true" />
        </span>
        <span class="grid w-6 place-items-center">
          <TbSquare class="size-2" stroke-width={1.25} aria-hidden="true" />
        </span>
        <span class="grid w-6 place-items-center">
          <TbX class="size-2.5" stroke-width={1.25} aria-hidden="true" />
        </span>
      </span>
    </div>
    {props.children}
  </div>
);

const WinLogo: Component<{ class: string }> = (props) => (
  <span class={`grid grid-cols-2 gap-px ${props.class}`}>
    <span class="bg-mock-windows" />
    <span class="bg-mock-windows" />
    <span class="bg-mock-windows" />
    <span class="bg-mock-windows" />
  </span>
);

export const DmgInDownloads = () => (
  <Scene label="Chief.dmg in the Downloads folder" delay={0}>
    <Finder place="Downloads">
      <div class="grid grid-cols-3 gap-x-1 gap-y-2.5">
        <FinderItem label="Chief.dmg" picked>
          <DiskImage />
        </FinderItem>
        <FinderItem label="Notes.pdf">
          <DocFile kind="PDF" color="#e5484d" />
        </FinderItem>
        <FinderItem label="Photo.jpg">
          <PhotoFile />
        </FinderItem>
        <FinderItem label="Plan.docx">
          <DocFile kind="DOC" color="#2f6fde" />
        </FinderItem>
        <FinderItem label="Q3.xlsx">
          <DocFile kind="XLS" color="#1f9d55" />
        </FinderItem>
        <FinderItem label="Assets.zip">
          <DocFile kind="ZIP" color="#7a808c" />
        </FinderItem>
      </div>
    </Finder>
  </Scene>
);

export const DragToApplications = () => (
  <Scene label="Drag the Chief icon into Applications" delay={0.4}>
    <div class="dl-window absolute inset-2.5 flex flex-col overflow-hidden rounded-[9px] bg-tile">
      <div class="relative flex h-5.5 shrink-0 items-center justify-center border-b border-primary-border">
        <span class="absolute left-2">
          <TrafficLights />
        </span>
        <span class="text-[8.5px] font-semibold text-secondary-text">Chief</span>
      </div>
      <div class="dl-dmg relative flex-1 text-[7.5px] font-medium">
        <span class="absolute top-10 left-9 flex w-11 flex-col items-center gap-1.5">
          <ChiefTile class="size-11 rounded-[10px]" />
          Chief
        </span>
        <svg
          viewBox="0 0 64 20"
          class="absolute top-13 left-21.5 w-16 text-muted-text"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M2 14Q31 1 58 12" stroke-dasharray="3 3" />
          <path d="M52 7l6 5l-7 3" />
        </svg>
        <span class="dl-drop dl-anim absolute top-8.5 left-37 size-14 rounded-[8px] bg-primary-text/10" />
        <span class="absolute top-10 left-38.5 flex w-11 flex-col items-center gap-1.5">
          <span class="dl-receive dl-anim flex h-11 items-center">
            <FolderGlyph />
          </span>
          Applications
        </span>
        <span class="dl-drag dl-anim absolute top-10 left-9 size-11">
          <span class="dl-ghost dl-anim absolute inset-0">
            <ChiefTile class="size-11 rounded-[10px]" />
            <span class="absolute -right-1 -bottom-1 grid size-3 place-items-center rounded-full bg-mock-online text-white">
              <TbPlus class="size-2" stroke-width={3} aria-hidden="true" />
            </span>
          </span>
          <Pointer class="dl-drag-cursor dl-anim top-6 left-6" />
        </span>
      </div>
    </div>
  </Scene>
);

const APPS: { label: string; icon: Glyph; from: string; to: string }[] = [
  { label: "Books", icon: TbBook, from: "#ffb340", to: "#f57c1f" },
  { label: "Calendar", icon: TbCalendar, from: "#ff6b6b", to: "#e5383b" },
];
const MORE_APPS: { label: string; icon: Glyph; from: string; to: string }[] = [
  { label: "Clock", icon: TbClock, from: "#5b5f6b", to: "#2c2f36" },
  { label: "Mail", icon: TbMail, from: "#5ab6ff", to: "#1f7ae0" },
  { label: "Maps", icon: TbMap, from: "#6fd38a", to: "#2fa152" },
  { label: "Music", icon: TbMusic, from: "#ff7a95", to: "#e83a5f" },
  { label: "Notes", icon: TbNotes, from: "#ffd96a", to: "#f2b81f" },
];

export const ChiefInApplications = () => (
  <Scene label="Chief in the Applications folder" delay={0.8}>
    <Finder place="Applications">
      <div class="grid grid-cols-4 gap-x-0.5 gap-y-1.5">
        <For each={APPS}>
          {(app) => (
            <FinderItem label={app.label}>
              <AppTile icon={app.icon} from={app.from} to={app.to} />
            </FinderItem>
          )}
        </For>
        <FinderItem label="Chief" picked>
          <ChiefTile class="size-6 rounded-md" />
        </FinderItem>
        <For each={MORE_APPS}>
          {(app) => (
            <FinderItem label={app.label}>
              <AppTile icon={app.icon} from={app.from} to={app.to} />
            </FinderItem>
          )}
        </For>
      </div>
    </Finder>
  </Scene>
);

const EXPLORER_PLACES: { label: string; icon: Glyph }[] = [
  { label: "Home", icon: TbHome },
  { label: "Desktop", icon: TbDeviceDesktop },
  { label: "Downloads", icon: TbCircleArrowDown },
];

export const SetupInDownloads = () => (
  <Scene label="Chief-setup.exe in the Downloads folder" delay={0}>
    <WinWindow title="Downloads" icon={<TbFolderFilled class="size-2.5 text-mock-folder" aria-hidden="true" />}>
      <div class="flex h-5 shrink-0 items-center gap-1 border-b border-primary-border px-2 text-muted-text">
        <TbChevronLeft class="size-2.5" aria-hidden="true" />
        <TbChevronRight class="size-2.5 opacity-40" aria-hidden="true" />
        <TbArrowUp class="size-2.5" aria-hidden="true" />
        <span class="ms-1 flex h-3.5 flex-1 items-center rounded-[3px] bg-primary-text/5 px-1.5 text-[7px] text-secondary-text">
          This PC › Downloads
        </span>
      </div>
      <div class="flex min-h-0 flex-1">
        <div class="flex w-17 shrink-0 flex-col gap-px border-e border-primary-border px-1 pt-1.5">
          <For each={EXPLORER_PLACES}>
            {(place) => (
              <span
                class="relative flex h-3.5 items-center gap-1 rounded-[3px] px-1.5 text-[7px]"
                classList={{ "bg-primary-text/8": place.label === "Downloads" }}
              >
                <Show when={place.label === "Downloads"}>
                  <span class="absolute inset-y-1 left-0 w-0.5 rounded-full bg-brand dark:bg-brand-border" />
                </Show>
                <Dynamic component={place.icon} class="size-2.5 shrink-0 text-secondary-text" aria-hidden="true" />
                {place.label}
              </span>
            )}
          </For>
        </div>
        <div class="flex min-w-0 flex-1 flex-col px-1.5 pt-1 text-[7px]">
          <span class="flex h-4 items-center justify-between border-b border-primary-border px-1 text-[6.5px] text-muted-text">
            <span>Name</span>
            <span>Date modified</span>
          </span>
          <span class="relative mt-0.5 flex h-4.5 items-center gap-1.5 rounded-[3px] px-1">
            <span class="dl-select dl-anim absolute inset-0 rounded-[3px] bg-brand/15 dark:bg-brand-border/25" />
            <ChiefTile class="relative size-2.5 rounded-xs" />
            <span class="relative font-medium">Chief-setup.exe</span>
            <span class="relative ms-auto text-muted-text">Today</span>
            <Pointer class="dl-cursor dl-anim top-1/2 left-10" />
          </span>
          <span class="flex h-4.5 items-center gap-1.5 px-1">
            <span class="grid size-2.5 place-items-center rounded-[1px] bg-mock-pdf text-[3px] font-bold text-white">
              PDF
            </span>
            Notes.pdf
            <span class="ms-auto text-muted-text">Yesterday</span>
          </span>
          <span class="flex h-4.5 items-center gap-1.5 px-1">
            <TbPhoto class="size-2.5 text-mock-windows" aria-hidden="true" />
            Photo.jpg
            <span class="ms-auto text-muted-text">Monday</span>
          </span>
        </div>
      </div>
    </WinWindow>
  </Scene>
);

export const SmartScreenRunAnyway = () => (
  <Scene label="SmartScreen warning — choose More info, then Run anyway" delay={0.4}>
    <div class="dl-window dl-win absolute inset-2.5 overflow-hidden rounded-[7px] bg-brand text-on-brand">
      <TbX class="absolute top-2 right-2 size-2.5 opacity-70" aria-hidden="true" />
      <span class="absolute top-3 left-3 text-[11px] leading-none font-semibold">Windows protected your PC</span>
      <span class="absolute top-8 left-3 w-50 text-[7px] leading-2.5 opacity-85">
        Microsoft Defender SmartScreen prevented an unrecognized app from starting.
      </span>
      <span class="absolute top-14 left-3 text-[7.5px] leading-2.5 font-semibold underline underline-offset-1">
        More info
      </span>
      <span class="dl-reveal dl-anim absolute top-18 left-3 text-[7px] leading-2.5 opacity-85">
        App: <span class="font-semibold">Chief-setup.exe</span>
      </span>
      <span class="absolute inset-x-0 bottom-8.5 border-t border-on-brand/20" />
      <span class="dl-reveal dl-anim absolute bottom-2 left-27.5 grid h-4.5 w-14 place-items-center rounded-[3px] border border-on-brand/60 text-[7px] font-semibold">
        <span class="dl-press dl-anim absolute inset-0 bg-on-brand/20" />
        <span class="relative">Run anyway</span>
      </span>
      <Pointer class="dl-smart-cursor dl-anim top-34.25 left-34.5" />
      <span class="absolute right-3 bottom-2 grid h-4.5 w-13 place-items-center rounded-[3px] border border-on-brand/60 text-[7px] font-semibold">
        Don't run
      </span>
    </div>
  </Scene>
);

const START_APPS: { label: string; icon: Glyph; from: string; to: string }[] = [
  { label: "Calendar", icon: TbCalendar, from: "#5ab6ff", to: "#1f7ae0" },
  { label: "Mail", icon: TbMail, from: "#4c9ef0", to: "#1a5fc2" },
];
const MORE_START_APPS: { label: string; icon: Glyph; from: string; to: string }[] = [
  { label: "Clock", icon: TbClock, from: "#5b5f6b", to: "#2c2f36" },
  { label: "Maps", icon: TbMap, from: "#ff6b6b", to: "#e5383b" },
  { label: "Music", icon: TbMusic, from: "#ff9b5a", to: "#f2661f" },
  { label: "Notes", icon: TbNotes, from: "#ffd96a", to: "#f2b81f" },
  { label: "Photos", icon: TbPhoto, from: "#6fc3ff", to: "#2f8ae0" },
];

const StartTile: ParentComponent<{ label: string; picked?: boolean }> = (props) => (
  <span class="relative flex flex-col items-center gap-0.5 rounded-sm py-0.5 text-[7px] leading-2.5">
    <Show when={props.picked}>
      <span class="dl-select dl-anim absolute inset-0 rounded-sm bg-primary-text/8" />
    </Show>
    <span class="relative">{props.children}</span>
    <span class="relative">{props.label}</span>
    <Show when={props.picked}>
      <Pointer class="dl-cursor dl-anim top-4 left-1/2" />
    </Show>
  </span>
);

export const ChiefInStartMenu = () => (
  <Scene label="Chief pinned in the Start menu" delay={0.8}>
    <div class="dl-window dl-win absolute inset-x-6 top-2.5 bottom-7 flex flex-col overflow-hidden rounded-[7px] bg-tile px-3 pt-2.5">
      <span class="flex h-4.5 shrink-0 items-center gap-1.5 rounded-full border border-primary-border bg-primary-text/5 px-2 text-[7px] text-muted-text">
        <TbSearch class="size-2.5" aria-hidden="true" />
        Search for apps, settings, and documents
      </span>
      <span class="mt-2 mb-1 text-[7.5px] leading-2.5 font-semibold">Pinned</span>
      <div class="grid grid-cols-4 gap-x-1 gap-y-0.5">
        <For each={START_APPS}>
          {(app) => (
            <StartTile label={app.label}>
              <AppTile icon={app.icon} from={app.from} to={app.to} />
            </StartTile>
          )}
        </For>
        <StartTile label="Chief" picked>
          <ChiefTile class="size-6 rounded-md" />
        </StartTile>
        <For each={MORE_START_APPS}>
          {(app) => (
            <StartTile label={app.label}>
              <AppTile icon={app.icon} from={app.from} to={app.to} />
            </StartTile>
          )}
        </For>
      </div>
    </div>
    <div class="dl-win absolute inset-x-0 bottom-0 flex h-5 items-center justify-center gap-2.5 border-t border-primary-border bg-tile/90">
      <WinLogo class="size-2.5" />
      <TbSearch class="size-2.5 text-secondary-text" aria-hidden="true" />
      <TbFolderFilled class="size-2.5 text-mock-folder" aria-hidden="true" />
      <span class="dl-launch dl-anim relative flex flex-col items-center">
        <ChiefTile class="size-3 rounded-[3px]" />
        <span class="absolute -bottom-1 h-0.5 w-1.5 rounded-full bg-brand dark:bg-brand-border" />
      </span>
    </div>
  </Scene>
);
